import React, { useCallback, type KeyboardEvent } from "react";
import { navigateToDashboardHref } from "../../lib/dashboardRowNavigation";
import { cn } from "../../lib/utils";

type ClickableTableRowProps = React.HTMLAttributes<HTMLTableRowElement> & {
  href: string;
  ariaLabel: string;
};

export const ClickableTableRow: React.FC<ClickableTableRowProps> = ({
  href,
  ariaLabel,
  className,
  onClick,
  onKeyDown,
  children,
  ...props
}) => {
  const navigate = useCallback(() => {
    navigateToDashboardHref(href);
  }, [href]);

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLTableRowElement>) => {
      onClick?.(event);
      if (event.defaultPrevented) return;
      navigate();
    },
    [navigate, onClick],
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLTableRowElement>) => {
      onKeyDown?.(event);
      if (event.defaultPrevented) return;
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        navigate();
      }
    },
    [navigate, onKeyDown],
  );

  return (
    <tr
      {...props}
      role="link"
      tabIndex={0}
      aria-label={ariaLabel}
      data-href={href}
      className={cn(
        "cursor-pointer border-b border-border transition-colors hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
        className,
      )}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
    >
      {children}
    </tr>
  );
};
